/**
 * The simulation's only source of randomness.
 *
 * `Math.random` is banned everywhere under src/physics and src/sim (enforced by
 * test/sim/decoupling.test.ts). Everything the simulation would otherwise have
 * to guess at draws from an instance of this instead, and that instance is part
 * of the world's state: it snapshots, clones, serializes and restores with
 * everything else. Two worlds built from the same seed and fed the same inputs
 * therefore produce bit-identical trajectories including the choices that are
 * random in the real game (which of the four demolition respawn points a car
 * comes back on, in particular — RocketSim has a generator for that and we had
 * pushed the decision onto the caller).
 *
 * ALGORITHM: mulberry32. A 32-bit state, one multiply-xor round, and a period of
 * 2^32 — long enough that a 10,000-tick episode uses a negligible slice of it.
 * It is chosen for being exactly reproducible: every operation below is an
 * integer op on a uint32, so it gives the same bits in every JS engine, which a
 * float-based generator would not. The same generator is already used by the
 * test suite, so a test can predict the sim's stream if it needs to.
 */

/** The generator's whole state: one uint32. */
export type RngState = number;

export class Rng {
  /** Current state. Public because it IS the serialized form. */
  state: RngState;

  constructor(seed = 0) {
    this.state = seed >>> 0;
  }

  /** Next raw 32-bit output. */
  nextUint32(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  }

  /** Uniform in [0, 1). */
  next(): number {
    return this.nextUint32() / 4294967296;
  }

  /**
   * Uniform integer in [0, n). Uses the top bits of the 32-bit word rather than
   * a modulo, so the low-order bias of a modulo reduction never shows up in a
   * small n like "which of four respawn points".
   */
  nextInt(n: number): number {
    if (n <= 1) return 0;
    return Math.floor((this.nextUint32() / 4294967296) * n);
  }

  /** Uniform in [lo, hi). */
  nextRange(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo);
  }

  clone(): Rng {
    const copy = new Rng(0);
    copy.state = this.state;
    return copy;
  }
}
